import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { find, map } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import {
  createCustomerBucketAccessGrant,
  customerBucketAccessGrantsQueryKey,
  disableCustomerBucketAccessGrant,
  getCustomerBucketAccessGrants,
  validateCustomerBucketAccessGrant,
} from 'api/customerBucketAccessGrants';
import CopyableText from 'components/atoms/CopyableText';
import { CustomerBucketAccessGrant } from 'interfaces/customerBucketAccessGrant';
import { Permission } from 'interfaces/permissionOption';
import { formatDateTimeForUser } from 'utils/dateTime';
import { getHttpStatusFromError, getQueryErrorMessage } from 'utils/httpError';
import { detectBrowserOperatingSystem, OperatingSystem } from 'utils/osDetection';
import { usePermissions } from 'utils/usePermissions';

interface StudyCustomerBucketAccessGrantsControlProps {
  awsProfileName?: string;
  selectedOs?: OperatingSystem;
  setAwsProfileName?: (awsProfileName: string) => void;
  setSelectedOs?: (operatingSystem: OperatingSystem) => void;
  studyId: string;
  studyName?: string;
}

const SUPPORTED_IAM_PRINCIPAL_PATTERN =
  /^arn:(aws|aws-us-gov|aws-cn):iam::[0-9]{12}:(role|user)\/[A-Za-z0-9+=,.@_/-]+$/;

const IAM_PRINCIPAL_HELPER_TEXT =
  'Use an IAM ARN in an AWS partition (for example arn:aws:iam, arn:aws-us-gov:iam, or arn:aws-cn:iam) and include role/ or user/. Temporary assumed-role session ARNs are not supported.';

type WindowsPrincipalType = 'iam' | 'assumed-role';

interface PrincipalDiscoveryInstructions {
  openTerminalGuidance: string;
}

export const StudyCustomerBucketAccessGrantsControl: React.FC<StudyCustomerBucketAccessGrantsControlProps> = ({
  awsProfileName,
  selectedOs,
  setAwsProfileName,
  setSelectedOs,
  studyId,
  studyName,
}) => {
  const { hasPermission } = usePermissions();
  const canManageGrants =
    hasPermission(Permission.EditLabCustomerBucketAccessGrants) ||
    hasPermission(Permission.CreateOwnCustomerBucketAccessGrants);

  if (!studyId || !canManageGrants) {
    return null;
  }

  return (
    <>
      <StudyCustomerBucketAccessGrantsForm
        awsProfileName={awsProfileName}
        selectedOs={selectedOs}
        setAwsProfileName={setAwsProfileName}
        setSelectedOs={setSelectedOs}
        studyId={studyId}
        studyName={studyName}
      />
    </>
  );
};

interface FormProps {
  awsProfileName?: string;
  selectedOs?: OperatingSystem;
  setAwsProfileName?: (awsProfileName: string) => void;
  setSelectedOs?: (operatingSystem: OperatingSystem) => void;
  studyId: string;
  studyName?: string;
}

const StudyCustomerBucketAccessGrantsForm: React.FC<FormProps> = ({
  awsProfileName,
  selectedOs,
  setAwsProfileName,
  setSelectedOs,
  studyId,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [principalArn, setPrincipalArn] = useState('');
  const [actionMessage, setActionMessage] = useState<{ severity: 'error' | 'success'; text: string } | null>(null);

  const grantsQuery = useQuery({
    queryKey: customerBucketAccessGrantsQueryKey(studyId),
    queryFn: ({ signal }) => getCustomerBucketAccessGrants(studyId, signal),
    enabled: Boolean(studyId),
  });

  const refreshGrants = async () => {
    await queryClient.invalidateQueries({ queryKey: customerBucketAccessGrantsQueryKey(studyId) });
  };

  const createMutation = useMutation(
    () =>
      createCustomerBucketAccessGrant(studyId, {
        principalArn: principalArn.trim(),
      }),
    {
      onSuccess: async (grant) => {
        setPrincipalArn('');
        setActionMessage({ severity: 'success', text: `${grant.displayName} can now upload to this study.` });
        await refreshGrants();
      },
      onError: (error) =>
        setActionMessage({
          severity: 'error',
          text: getUploadAccessErrorMessage(error, 'AWS upload access could not be added.'),
        }),
    }
  );

  const validateMutation = useMutation((grantId: string) => validateCustomerBucketAccessGrant(studyId, grantId), {
    onSuccess: async (result) => {
      const isReady = result.status === 'active';
      setActionMessage({
        severity: 'success',
        text: isReady ? 'AWS upload access is ready.' : 'AWS upload access is not ready yet.',
      });
      if (isReady) {
        enqueueSnackbar('AWS upload access is ready.', { variant: 'success' });
      }
      await refreshGrants();
    },
    onError: (error) =>
      setActionMessage({
        severity: 'error',
        text: getUploadAccessErrorMessage(error, 'AWS upload access could not be checked.'),
      }),
  });

  const disableMutation = useMutation((grantId: string) => disableCustomerBucketAccessGrant(studyId, grantId), {
    onSuccess: async (grant) => {
      setActionMessage({ severity: 'success', text: `${grant.displayName} no longer has upload access.` });
      await refreshGrants();
    },
    onError: (error) =>
      setActionMessage({
        severity: 'error',
        text: getUploadAccessErrorMessage(error, 'AWS upload access could not be removed.'),
      }),
  });

  const grants = grantsQuery.data || [];
  const normalizedPrincipalArn = principalArn.trim();
  const hasExistingGrantForPrincipal = Boolean(
    find(
      grants,
      (grant) =>
        grant.principalArn === normalizedPrincipalArn && (grant.status === 'pending' || grant.status === 'active')
    )
  );
  const createHelperText = hasExistingGrantForPrincipal
    ? 'This IAM role or user already has AWS upload access for this study.'
    : IAM_PRINCIPAL_HELPER_TEXT;
  const isCreateDisabled =
    !SUPPORTED_IAM_PRINCIPAL_PATTERN.test(normalizedPrincipalArn) ||
    hasExistingGrantForPrincipal ||
    createMutation.isLoading;
  const handleCreateGrantSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCreateDisabled) {
      return;
    }

    createMutation.mutate();
  };

  return (
    <Stack spacing={3}>
      <PrincipalDiscoveryHelp
        awsProfileName={awsProfileName}
        selectedOs={selectedOs}
        setAwsProfileName={setAwsProfileName}
        setSelectedOs={setSelectedOs}
      />

      {actionMessage ? <Alert severity={actionMessage.severity}>{actionMessage.text}</Alert> : null}

      <Grid component="form" container spacing={2} onSubmit={handleCreateGrantSubmit}>
        <Grid item xs={12}>
          <TextField
            label="AWS role or user ARN"
            value={principalArn}
            onChange={(event) => setPrincipalArn(event.target.value)}
            fullWidth
            error={
              Boolean(normalizedPrincipalArn) &&
              (!SUPPORTED_IAM_PRINCIPAL_PATTERN.test(normalizedPrincipalArn) || hasExistingGrantForPrincipal)
            }
            helperText={createHelperText}
          />
        </Grid>
        <Grid item xs={12}>
          <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={isCreateDisabled}>
            {createMutation.isLoading ? 'Saving...' : 'Add AWS access'}
          </Button>
        </Grid>
      </Grid>

      {grantsQuery.isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading AWS upload access...</Typography>
        </Stack>
      ) : grantsQuery.isError ? (
        <Alert severity="error">
          {getUploadAccessErrorMessage(grantsQuery.error, 'AWS upload access could not be loaded.')}
        </Alert>
      ) : grants.length === 0 ? (
        <Alert severity="info">No AWS roles or users have upload access for this study yet.</Alert>
      ) : (
        <List disablePadding aria-label="AWS roles and users with upload access">
          {map(grants, (grant, index) => (
            <React.Fragment key={grant.id}>
              {index > 0 ? <Divider /> : null}
              <GrantListItem
                grant={grant}
                onValidate={() => validateMutation.mutate(grant.id)}
                onDisable={() => disableMutation.mutate(grant.id)}
                isValidating={validateMutation.isLoading}
                isDisabling={disableMutation.isLoading}
              />
            </React.Fragment>
          ))}
        </List>
      )}
    </Stack>
  );
};

const GrantListItem = ({
  grant,
  onValidate,
  onDisable,
  isValidating,
  isDisabling,
}: {
  grant: CustomerBucketAccessGrant;
  onValidate: () => void;
  onDisable: () => void;
  isValidating: boolean;
  isDisabling: boolean;
}) => {
  const isDisabled = grant.status === 'disabled';
  return (
    <ListItem
      disableGutters
      secondaryAction={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<CheckCircleOutlineIcon />}
            disabled={isDisabled || isValidating}
            onClick={onValidate}
          >
            Check access
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<BlockIcon />}
            disabled={isDisabled || isDisabling}
            onClick={onDisable}
          >
            Remove this user&apos;s access
          </Button>
        </Stack>
      }
    >
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 20 }}>
            <Typography variant="subtitle2">{grant.displayName}</Typography>
            <Chip
              size="small"
              label={grant.status === 'active' ? 'ready' : grant.status}
              color={grant.status === 'active' ? 'success' : 'default'}
            />
          </Stack>
        }
        secondary={
          <Stack spacing={0.5} sx={{ pr: 20 }}>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {grant.principalArn}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload bucket: {grant.customerBucketName}
              {grant.lastValidatedAt ? ` · Checked ${formatDateTimeForUser(grant.lastValidatedAt)}` : ''}
            </Typography>
          </Stack>
        }
      />
    </ListItem>
  );
};

const getUploadAccessErrorMessage = (error: unknown, fallbackMessage: string) => {
  const message = getQueryErrorMessage(error, fallbackMessage);

  if (message.includes('backend AWS credentials are expired or invalid')) {
    return 'Nucleai could not set up upload access because the backend AWS credentials are expired or invalid. Refresh the backend AWS session, restart the backend, and try again.';
  }

  if (message.includes('Destination policy reconciliation failed')) {
    return 'Nucleai could not finish setting up upload access in AWS. Please try again or contact support.';
  }

  if (message.includes('customer bucket must be provisioned')) {
    return 'The lab upload bucket is not ready yet. Please contact support before adding AWS upload access.';
  }

  if (message === 'Internal Server Error' || getHttpStatusFromError(error) === 500) {
    return 'Nucleai could not set up upload access because the backend returned an unexpected error while updating AWS access. Check the backend logs for AWS credential or database permission details, then try again.';
  }

  return message;
};

const buildAwsCliPrefix = (awsProfileName?: string) => `aws${awsProfileName ? ` --profile ${awsProfileName}` : ''}`;

const buildAwsPrincipalLookupCommand = (awsProfileName?: string) => {
  const awsCliPrefix = buildAwsCliPrefix(awsProfileName);

  return `CALLER_ARN="$(${awsCliPrefix} sts get-caller-identity --query Arn --output text)"; if echo "$CALLER_ARN" | grep -Eq '^arn:aws(-[a-z-]+)?:iam::[0-9]{12}:(role|user)/'; then echo "$CALLER_ARN"; elif echo "$CALLER_ARN" | grep -Eq '^arn:aws(-[a-z-]+)?:sts::[0-9]{12}:assumed-role/'; then ROLE_NAME="$(echo "$CALLER_ARN" | sed -E 's#^arn:([^:]+):sts::[0-9]{12}:assumed-role/([^/]+)/.*$#\\2#')"; ${awsCliPrefix} iam list-roles --query "Roles[?RoleName=='\${ROLE_NAME}'].Arn | [0]" --output text; else echo "Unsupported caller ARN: $CALLER_ARN" >&2; fi`;
};

const buildAwsWindowsCallerArnCommand = (awsProfileName?: string) =>
  `${buildAwsCliPrefix(awsProfileName)} sts get-caller-identity --query Arn --output text`;

const buildAwsRoleArnLookupCommand = (roleName: string, awsProfileName?: string) =>
  `${buildAwsCliPrefix(awsProfileName)} iam list-roles --query "Roles[?RoleName=='${
    roleName || 'ROLE_NAME'
  }'].Arn | [0]" --output text`;

const PRINCIPAL_DISCOVERY_INSTRUCTIONS: Record<OperatingSystem, PrincipalDiscoveryInstructions> = {
  mac: {
    openTerminalGuidance:
      'Open Terminal and run this one-line command with the same AWS profile you use for uploads. It prints the IAM role or user ARN to paste below.',
  },
  windows: {
    openTerminalGuidance:
      'Run this with the same AWS profile you use for uploads, then paste the printed ARN to the "AWS role or user ARN" box below.',
  },
  linux: {
    openTerminalGuidance:
      'Open a terminal and run this one-line command with the same AWS profile you use for uploads. It prints the IAM role or user ARN to paste below.',
  },
};

const PrincipalDiscoveryHelp = ({
  awsProfileName,
  selectedOs: selectedOsFromParent,
  setAwsProfileName,
  setSelectedOs: setSelectedOsFromParent,
}: {
  awsProfileName?: string;
  selectedOs?: OperatingSystem;
  setAwsProfileName?: (awsProfileName: string) => void;
  setSelectedOs?: (operatingSystem: OperatingSystem) => void;
}) => {
  const [localOs, setLocalOs] = useState<OperatingSystem>(detectBrowserOperatingSystem);
  const selectedOs = selectedOsFromParent || localOs;
  const setSelectedOs = setSelectedOsFromParent || setLocalOs;
  const [windowsRoleName, setWindowsRoleName] = useState('');
  const [windowsPrincipalType, setWindowsPrincipalType] = useState<WindowsPrincipalType>('iam');
  const instructions = PRINCIPAL_DISCOVERY_INSTRUCTIONS[selectedOs];
  const trimmedAwsProfileName = awsProfileName?.trim() || '';
  const trimmedWindowsRoleName = windowsRoleName.trim();
  const principalLookupCommand = buildAwsPrincipalLookupCommand(trimmedAwsProfileName || undefined);
  const windowsCallerArnCommand = buildAwsWindowsCallerArnCommand(trimmedAwsProfileName || undefined);
  const windowsRoleArnLookupCommand = buildAwsRoleArnLookupCommand(
    trimmedWindowsRoleName,
    trimmedAwsProfileName || undefined
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2">Find the IAM role or user ARN</Typography>
        <Typography variant="body2" color="text.secondary">
          Open a terminal and run the command for your operating system. It prints the IAM role or user ARN for the
          profile you chose. Paste that ARN in the field below.
        </Typography>
        {setAwsProfileName ? (
          <TextField
            label="AWS profile name"
            value={awsProfileName || ''}
            onChange={(event) => setAwsProfileName(event.target.value)}
            fullWidth
            placeholder="my-upload-profile"
            helperText="Optional. If you run AWS CLI with a named profile, enter it here so these commands include --profile."
          />
        ) : null}
        <OsTabs
          ariaLabel="Operating system for AWS principal lookup instructions"
          selectedOs={selectedOs}
          setSelectedOs={setSelectedOs}
        />
        <Typography variant="body2" color="text.secondary">
          {instructions.openTerminalGuidance}
        </Typography>
        {selectedOs === 'windows' ? (
          <>
            <CliCommandBlock label="1. Print the ARN from your current AWS profile" command={windowsCallerArnCommand} />
            <Typography variant="body2" color="text.secondary">
              2. Choose the ARN type that the command printed.
            </Typography>
            <RadioGroup
              value={windowsPrincipalType}
              onChange={(event) => setWindowsPrincipalType(event.target.value as WindowsPrincipalType)}
            >
              <FormControlLabel
                value="iam"
                control={<Radio />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    3. The ARN starts with <code>arn:aws:iam::</code>. Paste that ARN in the field below.
                  </Typography>
                }
              />
              <FormControlLabel
                value="assumed-role"
                control={<Radio />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    3. The ARN starts with <code>arn:aws:sts::</code>. Copy the role name after{' '}
                    <code>assumed-role/</code> and before the next <code>/</code>.
                  </Typography>
                }
              />
            </RadioGroup>
            {windowsPrincipalType === 'assumed-role' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  4. Paste that role name below. The second command updates automatically so you can copy and run it to
                  get the IAM role ARN.
                </Typography>
                <TextField
                  label="4a. Role name from assumed-role ARN"
                  value={windowsRoleName}
                  onChange={(event) => setWindowsRoleName(event.target.value)}
                  fullWidth
                  placeholder="AWSReservedSSO_Example_1234567890abcdef"
                />
                <CliCommandBlock
                  label="4b. Run this command to get the IAM role ARN"
                  command={windowsRoleArnLookupCommand}
                />
              </>
            ) : null}
          </>
        ) : (
          <CliCommandBlock label="Print the ARN to paste below" command={principalLookupCommand} />
        )}
        <Typography variant="body2">
          If role lookup is blocked in your AWS account, ask your AWS administrator for the IAM role ARN or IAM user ARN
          behind your upload profile.
        </Typography>
      </Stack>
    </Box>
  );
};

const OsTabs = ({
  ariaLabel,
  selectedOs,
  setSelectedOs,
}: {
  ariaLabel: string;
  selectedOs: OperatingSystem;
  setSelectedOs: (operatingSystem: OperatingSystem) => void;
}) => (
  <Tabs
    value={selectedOs}
    onChange={(_event, value: OperatingSystem) => setSelectedOs(value)}
    aria-label={ariaLabel}
    variant="scrollable"
    allowScrollButtonsMobile
  >
    <Tab value="mac" label="macOS" />
    <Tab value="windows" label="Windows" />
    <Tab value="linux" label="Linux" />
  </Tabs>
);

const CliCommandBlock = ({ label, command }: { label?: React.ReactNode; command: string }) => (
  <Box>
    {Boolean(label) && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
    )}
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        px: 1.5,
        py: 1,
      }}
    >
      <CopyableText
        copyWithButton="right"
        copyButtonVisibility="always"
        textForCopy={command}
        tooltipText="Copy command"
        variant="body2"
        component="code"
        whiteSpace="pre-wrap"
        sx={{
          display: 'block',
          fontFamily: 'monospace',
          overflow: 'auto',
          textOverflow: 'clip',
        }}
      >
        {command}
      </CopyableText>
    </Box>
  </Box>
);
